"use client";

import { Copy } from "iconsax-reactjs";
import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckIcon, type CheckIconHandle } from "@/components/ui/CheckIcon";
import { cn } from "@/lib/utils";

interface CopyableTextProps {
  text: string;
  size?: number;
  className?: string;
  textClassName?: string;
  buttonClassName?: string;
  checkIconClassName?: string;
  copyIconClassName?: string;
}

export const CopyableText: React.FC<CopyableTextProps> = ({
  text,
  size = 16,
  className,
  textClassName,
  buttonClassName,
  checkIconClassName,
  copyIconClassName,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const checkIconRef = useRef<CheckIconHandle | null>(null);

  const handleCopy = useCallback(() => {
    if (text) {
      // Clear any existing text selection to prevent copying selected text instead
      window.getSelection()?.removeAllRanges();

      setIsCopied(true);
      setTimeout(() => {
        checkIconRef.current?.startAnimation();
      }, 0);

      setTimeout(() => setIsCopied(false), 3000);
      navigator.clipboard.writeText(text.trim());
    }
  }, [text]);

  return (
    <div
      className={cn("flex items-center justify-between space-x-2", className)}
    >
      <p className={cn("text-inherit mt-1", textClassName)}>{text}</p>
      {isCopied ? (
        <Button
          size={"icon"}
          className={cn("p-0", buttonClassName)}
          variant={"light"}
          type="button"
        >
          <CheckIcon
            className={cn("text-green-500", checkIconClassName)}
            ref={checkIconRef}
            size={size}
          />
        </Button>
      ) : (
        <Button
          size={"icon"}
          className={cn("p-0 text-inherit", buttonClassName)}
          variant={"light"}
          onClick={handleCopy}
          type="button"
        >
          <Copy size={size} className={copyIconClassName} />
        </Button>
      )}
    </div>
  );
};
